import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { CurrentUser } from '@/types/domain'

/* The per-user language switch (FR-58). Russian and English are both
 * first-class; this is a preference on the user record, not a client-side
 * toggle, so it follows the user to whichever machine they sign in from.
 *
 * The session cache is updated optimistically: the switch sits in persistent
 * chrome and on the profile page, and a control that waits a round-trip to move
 * reads as broken. On failure the previous session is restored and the caller
 * surfaces the error. */

export function useUpdateLanguage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (language: CurrentUser['language']) =>
      USE_MOCKS
        ? Promise.resolve()
        : api.patch<void>('/session/language', { language }),

    onMutate: async (language) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.session })
      const previous = queryClient.getQueryData<CurrentUser>(queryKeys.session)

      queryClient.setQueryData<CurrentUser>(queryKeys.session, (user) =>
        user ? { ...user, language } : user,
      )

      return { previous }
    },

    onError: (_error, _language, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.session, context.previous)
      }
    },

    // Reconcile with the server's copy. Skipped on mocks, where refetching
    // replays the fixture and would undo the optimistic update.
    onSettled: () => {
      if (!USE_MOCKS) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.session })
      }
    },
  })
}
